import i18n from "@/i18n.ts";
import { DefaultTimezone } from "@/services/timezone-service.ts";
import {
  ApiCalendar,
  ApiCalendarProviders,
  ApiConferenceProviders,
} from "@/types/calendar.types.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import {
  ApiEmployee,
  ApiEmployeeBase,
  ApiEmployeeShort,
} from "@/types/employees/employee.types.ts";
import { ApiJobShort } from "@/types/recruit/job.types.ts";
import { RuleGroup, RuleGroupShort } from "@/types/rule.types.ts";
import { addMinutes, roundToNearestMinutes } from "date-fns";

export enum ApiEventTypes {
  interview = "interview",
  default = "default",
}

export enum CalendarEventType {
  event = "event",
  time_off = "time_off",
  holiday = "holiday",
}

export enum ApiEventAttendeeResponseStatuses {
  accepted = "accepted",
  declined = "declined",
  needs_action = "needs_action",
  tentative = "tentative",
}

export interface ApiEventInterviewSavePayload {
  candidate_id: number;
  applicant_id: number;
  job_id: number;
  hide_salary: boolean;
}

export interface ApiEventInterview {
  candidate_id: number;
  hide_salary: boolean;
  applicant_id: number;
  job_id: number;
  job: ApiJobShort;
  candidate_name: string;
}

export interface ApiEventAttendee {
  employee?: ApiEmployeeShort;
  email?: string;
  response_status: ApiEventAttendeeResponseStatuses;
  is_organizer: boolean;

  runtime_only_employee?: ApiEmployee;
}

export enum ApiEventVisibility {
  public = "public",
  private = "private",
}

export interface ApiEventConferenceData {
  title: string;
  uri: string;
  label: string;
}

export interface ApiEvent {
  id: number;
  type: ApiEventTypes;
  title: string;
  timezone: string; //"Africa/Abidjan", // формат тут https://en.wikipedia.org/wiki/List_of_tz_database_time_zones
  description: string;
  location?: string;
  organizer: ApiEventAttendee;
  calendar: ApiCalendar;
  is_all_day: boolean;

  datetime_start: Date;
  datetime_end: Date;
  visibility: ApiEventVisibility;
  provider: ApiCalendarProviders;
  interview?: ApiEventInterview;
  attendees: ApiEventAttendee[];
  recurrence: string[];

  conference_data?: ApiEventConferenceData;
  conference_provider: ApiConferenceProviders | null;

  external_event_link?: string;
}

export interface ApiEventEdit {
  type: ApiEventTypes;
  title: string;
  timezone: string; //"Africa/Abidjan", // формат тут https://en.wikipedia.org/wiki/List_of_tz_database_time_zones
  description: string;
  location?: string;
  calendar: ApiCalendar;
  is_all_day: boolean;
  conference_provider: ApiConferenceProviders | null;
  recurrence: string[];

  datetime_start: Date;
  datetime_end: Date;
  visibility: ApiEventVisibility;
  provider: ApiCalendarProviders;
  interview?: ApiEventInterviewSavePayload;
  attendees: ApiEventAttendee[];

  suggestedAttendees?: ApiEventAttendee[];
  attendeeGroups?: RuleGroup[];
}

export const mockEventEdit: ApiEventEdit = {
  type: ApiEventTypes.default,
  is_all_day: false,
  title: "",
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  description: "",
  location: "",
  calendar: {
    id: 0,
    title: "",
    timezone: DefaultTimezone,
    is_default: false,
    provider: ApiCalendarProviders.system,
    conference_provider: null,
    conference_providers: [],
  },
  datetime_start: addMinutes(roundToNearestMinutes(new Date(), { nearestTo: 30 }), 0),
  datetime_end: addMinutes(roundToNearestMinutes(new Date(), { nearestTo: 30 }), 30),
  visibility: ApiEventVisibility.public,
  provider: ApiCalendarProviders.system,
  interview: undefined,
  attendees: [],
  conference_provider: ApiConferenceProviders.google_meet,
  recurrence: [],
};

export const getMockEventEdit = (organizer: ApiEmployeeBase, title: string = "") => {
  return {
    ...mockEventEdit,
    title: title,
    timezone: organizer.timezone || mockEventEdit.timezone,
    datetime_start: addMinutes(roundToNearestMinutes(new Date(), { nearestTo: 30 }), 0),
    datetime_end: addMinutes(roundToNearestMinutes(new Date(), { nearestTo: 30 }), 30),
    attendees: [
      {
        employee: organizer,
        response_status: ApiEventAttendeeResponseStatuses.accepted,
        is_organizer: true,
      },
    ],
  } as ApiEventEdit;
};

export interface ApiEventFilter {
  text?: string;
  employee_ids?: number[];
  date_start?: Date;
  date_end?: Date;
  types?: ApiEventTypes[];
}

export interface ApiEventAttendeeSavePayload {
  employee_id?: number;
  email?: string;
  is_organizer: boolean;
}

export interface ApiEventSavePayload {
  type: ApiEventTypes;
  title: string;
  timezone: string; //"Africa/Abidjan", // формат тут https://en.wikipedia.org/wiki/List_of_tz_database_time_zones
  datetime_start: Date;
  datetime_end: Date;
  description: string;
  location?: string;
  calendar_id: number;
  visibility: ApiEventVisibility;
  is_all_day: boolean;
  is_conference: boolean;
  conference_provider: ApiConferenceProviders | null;
  recurrence: string[];
  interview?: ApiEventInterviewSavePayload;

  attendees: ApiEventAttendeeSavePayload[];
  attendee_rule_groups?: RuleGroupShort[];
}

export const getDescriptionForAttendee = (attendee: ApiEventAttendee) => {
  const texts: string[] = [];
  if (attendee.is_organizer) {
    texts.push(i18n.t("calendar:organizer"));
  }
  if (!attendee.employee && !attendee.runtime_only_employee) {
    texts.push(i18n.t("calendar:external_contact"));
  }
  if (attendee.runtime_only_employee) {
    texts.push(getEmployeePositionTitle(attendee.runtime_only_employee));
  }
  return texts;
};

export const getAttendeeResponseText = (status: ApiEventAttendeeResponseStatuses) => {
  switch (status) {
    case ApiEventAttendeeResponseStatuses.accepted:
      return i18n.t("calendar:response_status_accepted");
    case ApiEventAttendeeResponseStatuses.declined:
      return i18n.t("calendar:response_status_needs_declined");
    case ApiEventAttendeeResponseStatuses.needs_action:
      return i18n.t("calendar:response_status_needs_action");
    case ApiEventAttendeeResponseStatuses.tentative:
      return i18n.t("calendar:response_status_needs_tentative");
  }
};

export interface ApiEventsBusyFilter {
  employee_ids: number[];
  date_start: string;
  date_end: string;
}

export interface ApiEventBusyInterval {
  datetime_start: Date;
  datetime_end: Date;
}

export interface ApiEmployeeBusyIntervals {
  employee_id: number;
  intervals: ApiEventBusyInterval[];
}
